import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  ClipboardList, Plus, Search, Eye, Download, Trash2, 
  FileText, ChevronLeft, X, Package, Calendar, Euro,
  Truck, Check, AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

// Helper per ottenere gli headers con tenant_db
const getHeaders = () => {
  const token = localStorage.getItem('token') || localStorage.getItem('tenant_token');
  const headers = { 'Authorization': `Bearer ${token}` };
  
  let tenantDb = localStorage.getItem('tenant_db');
  if (!tenantDb) {
    try {
      const tenantList = JSON.parse(localStorage.getItem('tenant_list') || '[]');
      if (tenantList.length > 0 && tenantList[0].database_name) {
        tenantDb = tenantList[0].database_name;
      }
    } catch (e) {}
  }
  if (!tenantDb) {
    try {
      const tenantUser = JSON.parse(localStorage.getItem('tenant_user') || '{}');
      if (tenantUser.tenants && tenantUser.tenants[0]) {
        tenantDb = tenantUser.tenants[0].database_name;
      }
    } catch (e) {}
  }
  
  if (tenantDb) {
    headers['X-Tenant-DB'] = tenantDb.replace(/^tenant_/, '');
  }
  return headers;
};

const DDTPage = () => {
  const [ddtList, setDdtList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDDT, setSelectedDDT] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  useEffect(() => {
    loadDDT();
  }, []);

  const loadDDT = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/gestione/ddt`, {
        headers: getHeaders()
      });
      setDdtList(response.data.ddt || []);
    } catch (error) {
      console.error('Error loading DDT:', error);
      // Non mostrare errore toast - semplicemente mostra lista vuota
      setDdtList([]);
    } finally {
      setLoading(false);
    }
  };

  const deleteDDT = async (id) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo DDT?')) return;
    try {
      await axios.delete(`${API}/gestione/ddt/${id}`, {
        headers: getHeaders()
      });
      toast.success('DDT eliminato');
      setShowDetailModal(false);
      setSelectedDDT(null);
      loadDDT();
    } catch (error) {
      toast.error('Errore eliminazione DDT');
    }
  };

  const openDetail = (ddt) => {
    setSelectedDDT(ddt);
    setShowDetailModal(true);
  };

  const closeDetail = () => {
    setShowDetailModal(false);
    setSelectedDDT(null);
  };

  const filteredDDT = ddtList.filter(ddt => 
    !searchTerm || 
    ddt.fornitore?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    ddt.numero?.toString().includes(searchTerm)
  );

  const getStatusBadge = (status) => {
    switch (status) {
      case 'confermato': 
        return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-500/20 text-green-400 lg:bg-green-100 lg:text-green-700">Confermato</span>;
      case 'bozza': 
        return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-500/20 text-yellow-400 lg:bg-yellow-100 lg:text-yellow-700">Bozza</span>;
      case 'annullato': 
        return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-500/20 text-red-400 lg:bg-red-100 lg:text-red-700">Annullato</span>;
      default: 
        return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-slate-500/20 text-slate-400 lg:bg-gray-100 lg:text-gray-700">{status || 'N/D'}</span>;
    }
  };

  return (
    <div className="min-h-screen bg-transparent lg:bg-gray-50">
      {/* Header Mobile */}
      <div className="lg:hidden p-4 pt-2 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-white">Bolle / DDT</h1>
            <p className="text-sm text-slate-400">Documenti di trasporto</p>
          </div>
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
            <Truck className="h-6 w-6 text-white" />
          </div>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
          <input 
            placeholder="Cerca fornitore o numero..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-3 rounded-xl bg-slate-800 border border-slate-700 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-2">
          <div className="bg-slate-800/80 rounded-xl p-3 text-center border border-slate-700/50">
            <p className="text-lg font-bold text-white">{ddtList.length}</p>
            <p className="text-[10px] text-slate-400">Totale DDT</p>
          </div>
          <div className="bg-slate-800/80 rounded-xl p-3 text-center border border-slate-700/50">
            <p className="text-lg font-bold text-green-400">{ddtList.filter(d => d.status === 'confermato').length}</p>
            <p className="text-[10px] text-slate-400">Confermati</p>
          </div>
          <div className="bg-slate-800/80 rounded-xl p-3 text-center border border-slate-700/50">
            <p className="text-lg font-bold text-blue-400">€{ddtList.reduce((sum, d) => sum + (d.totale || 0), 0).toFixed(0)}</p>
            <p className="text-[10px] text-slate-400">Valore Tot.</p>
          </div>
        </div>
      </div>

      {/* Header Desktop */}
      <div className="hidden lg:block p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Gestione Bolle / DDT</h1>
            <p className="text-sm text-gray-500">Gestisci i documenti di trasporto</p>
          </div>
        </div>
        <div className="relative max-w-md">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input 
            placeholder="Cerca per fornitore o numero DDT..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-11 pr-4 py-3 rounded-xl bg-gray-100 border-0 focus:bg-white focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      {/* Content */}
      <div className="px-4 lg:px-6 pb-24 lg:pb-6">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          </div>
        ) : filteredDDT.length === 0 ? (
          <div className="text-center py-12 bg-slate-800/60 backdrop-blur-sm lg:bg-white rounded-2xl border border-slate-700 lg:border-gray-100">
            <div className="w-16 h-16 bg-blue-500/20 lg:bg-blue-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <ClipboardList className="h-8 w-8 text-blue-400" />
            </div>
            <p className="text-slate-400 lg:text-gray-500 mb-2">Nessun DDT trovato</p>
            <p className="text-xs text-slate-500 lg:text-gray-400">Carica le fatture per generare DDT automatici</p>
          </div>
        ) : (
          <>
            {/* Mobile: Card List */}
            <div className="lg:hidden space-y-3">
              {filteredDDT.map((ddt) => (
                <div 
                  key={ddt.id}
                  onClick={() => openDetail(ddt)}
                  className="bg-slate-800/80 rounded-2xl p-4 border border-slate-700/50 active:bg-slate-700/50 transition-all"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-bold text-blue-400">DDT #{ddt.numero}</span>
                        {getStatusBadge(ddt.status)}
                      </div>
                      <p className="text-sm text-white font-medium">{ddt.fornitore}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-green-400 text-lg">€{(ddt.totale || 0).toFixed(2)}</p>
                      <p className="text-xs text-slate-400">{ddt.n_prodotti || 0} prodotti</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between pt-3 border-t border-slate-700/50">
                    <div className="flex items-center gap-3 text-xs text-slate-400">
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {ddt.data_ddt}
                      </span>
                    </div>
                    <ChevronLeft className="h-5 w-5 text-slate-500 rotate-180" />
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop: Table */}
            <div className="hidden lg:block bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
              <table className="w-full">
                <thead className="bg-gradient-to-r from-blue-50 to-purple-50 border-b border-blue-100">
                  <tr>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-blue-900 uppercase">N° DDT</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-blue-900 uppercase">Status</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-blue-900 uppercase">Fornitore</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-blue-900 uppercase">Data DDT</th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-blue-900 uppercase">Totale</th>
                    <th className="text-center py-3 px-4 text-xs font-semibold text-blue-900 uppercase">Prodotti</th>
                    <th className="text-center py-3 px-4 text-xs font-semibold text-blue-900 uppercase">Azioni</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredDDT.map((ddt) => (
                    <tr key={ddt.id} className="hover:bg-blue-50/50 transition-colors">
                      <td className="py-3 px-4 font-medium text-blue-900">{ddt.numero}</td>
                      <td className="py-3 px-4">{getStatusBadge(ddt.status)}</td>
                      <td className="py-3 px-4">{ddt.fornitore}</td>
                      <td className="py-3 px-4 text-gray-500">{ddt.data_ddt}</td>
                      <td className="py-3 px-4 text-right font-medium text-blue-900">€{(ddt.totale || 0).toFixed(2)}</td>
                      <td className="py-3 px-4 text-center">
                        <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                          {ddt.n_prodotti || 0}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <button 
                          onClick={() => openDetail(ddt)}
                          className="px-3 py-1.5 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-lg text-sm transition-colors"
                        >
                          Apri
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* Detail Modal - FULLSCREEN su mobile */}
      {showDetailModal && selectedDDT && (
        <div className="fixed inset-0 bg-slate-900/95 backdrop-blur-sm lg:bg-black/70 flex flex-col lg:items-center lg:justify-center z-[100]">
          <div className="flex-1 lg:flex-none bg-slate-900/95 lg:bg-white lg:rounded-2xl w-full lg:max-w-2xl lg:max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="p-4 border-b border-slate-700 lg:border-gray-100 flex items-center justify-between bg-slate-900 lg:bg-gradient-to-r lg:from-blue-50 lg:to-purple-50 flex-shrink-0">
              <div className="flex items-center gap-3">
                <button 
                  onClick={closeDetail} 
                  className="p-2 bg-slate-800/70 backdrop-blur-sm lg:bg-white/50 hover:bg-slate-700 lg:hover:bg-white/70 rounded-xl"
                >
                  <ChevronLeft className="h-5 w-5 text-white lg:text-gray-500" />
                </button>
                <div>
                  <h3 className="font-bold text-white lg:text-gray-900">DDT #{selectedDDT.numero}</h3>
                  <p className="text-xs text-blue-400 lg:text-blue-600">{selectedDDT.fornitore}</p>
                </div>
              </div>
              <button 
                onClick={closeDetail} 
                className="p-2 bg-slate-800/70 backdrop-blur-sm lg:bg-white/50 hover:bg-slate-700 lg:hover:bg-white/70 rounded-xl hidden lg:block"
              >
                <X className="h-4 w-4 text-white lg:text-gray-500" />
              </button>
            </div>
            
            {/* Content */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 pb-32 lg:pb-4">
              {/* Info Cards */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-slate-800/70 backdrop-blur-sm lg:bg-blue-50 rounded-xl p-3 border border-slate-700 lg:border-transparent">
                  <div className="flex items-center gap-2 mb-1">
                    <Calendar className="h-4 w-4 text-blue-400 lg:text-blue-600" />
                    <p className="text-xs text-slate-400 lg:text-blue-600">Data DDT</p>
                  </div>
                  <p className="font-semibold text-white lg:text-blue-900">{selectedDDT.data_ddt}</p>
                </div>
                <div className="bg-slate-800/70 backdrop-blur-sm lg:bg-green-50 rounded-xl p-3 border border-slate-700 lg:border-transparent">
                  <div className="flex items-center gap-2 mb-1">
                    <Euro className="h-4 w-4 text-green-400 lg:text-green-600" />
                    <p className="text-xs text-slate-400 lg:text-green-600">Totale</p>
                  </div>
                  <p className="font-bold text-green-400 lg:text-green-900 text-lg">€{(selectedDDT.totale || 0).toFixed(2)}</p>
                </div>
              </div>

              {/* Status */}
              <div className="bg-slate-800/70 backdrop-blur-sm lg:bg-gray-50 rounded-xl p-3 border border-slate-700 lg:border-transparent flex items-center justify-between">
                <span className="text-sm text-slate-400 lg:text-gray-500">Stato</span>
                {getStatusBadge(selectedDDT.status)}
              </div>
              
              {/* Prodotti */}
              {selectedDDT.prodotti && selectedDDT.prodotti.length > 0 && (
                <div className="bg-slate-800/70 backdrop-blur-sm lg:bg-gray-50 rounded-xl p-4 border border-slate-700 lg:border-transparent">
                  <h4 className="font-semibold text-white lg:text-gray-900 mb-3 flex items-center gap-2">
                    <Package className="h-4 w-4 text-blue-400 lg:text-blue-600" />
                    Prodotti ({selectedDDT.prodotti.length})
                  </h4>
                  <div className="space-y-2">
                    {selectedDDT.prodotti.map((p, i) => (
                      <div 
                        key={i} 
                        className="p-3 bg-slate-700/50 lg:bg-white rounded-xl flex justify-between items-center border border-slate-600/50 lg:border-gray-100"
                      >
                        <div>
                          <p className="font-medium text-white lg:text-gray-900 text-sm">{p.nome}</p>
                          <p className="text-xs text-slate-400 lg:text-gray-500">
                            {p.quantita} {p.unita_misura}
                            {p.lotto && <span className="ml-2">• Lotto: {p.lotto}</span>}
                          </p>
                        </div>
                        <p className="font-medium text-blue-400 lg:text-blue-600">€{(p.totale || 0).toFixed(2)}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Note */}
              {selectedDDT.note && (
                <div className="bg-slate-800/70 backdrop-blur-sm lg:bg-gray-50 rounded-xl p-4 border border-slate-700 lg:border-transparent">
                  <h4 className="font-semibold text-white lg:text-gray-900 mb-2 text-sm">Note</h4>
                  <p className="text-sm text-slate-400 lg:text-gray-500">{selectedDDT.note}</p>
                </div>
              )}
            </div>

            {/* Footer - FISSO in basso su mobile SOPRA la bottom nav */}
            <div className="fixed lg:relative lg:bottom-0 safe-footer left-0 right-0 p-3 border-t border-slate-700 lg:border-gray-100 flex gap-2 flex-shrink-0 bg-slate-900/95 lg:bg-white z-[101]">
              <button
                onClick={() => {/* Download PDF */}}
                className="flex-1 py-3 lg:py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium text-sm transition-colors flex items-center justify-center gap-2"
              >
                <Download className="h-4 w-4" />
                Scarica PDF
              </button>
              <button
                onClick={() => deleteDDT(selectedDDT.id)}
                className="px-4 py-3 lg:py-2.5 bg-red-500/20 lg:bg-red-100 hover:bg-red-500/30 lg:hover:bg-red-200 text-red-400 lg:text-red-700 rounded-xl font-medium text-sm transition-colors border border-red-500/30 lg:border-transparent flex items-center justify-center gap-1"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DDTPage;
